import React, { useCallback, useRef, useState } from 'react'

export const BANNER_IMAGE_FIT_OPTIONS = [
  { value: 'cover', label: 'Fill frame (may crop edges)' },
  { value: 'contain', label: 'Show full image (may letterbox)' },
]

export const BANNER_IMAGE_FOCUS_OPTIONS = [
  { value: 'center center', label: 'Center' },
  { value: 'top center', label: 'Top' },
  { value: 'bottom center', label: 'Bottom' },
  { value: 'center left', label: 'Left' },
  { value: 'center right', label: 'Right' },
  { value: 'top left', label: 'Top left' },
  { value: 'top right', label: 'Top right' },
  { value: 'bottom left', label: 'Bottom left' },
  { value: 'bottom right', label: 'Bottom right' },
]

const PRESET_POSITION_VALUES = new Set(
  BANNER_IMAGE_FOCUS_OPTIONS.map(o => o.value),
)

function parsePositionToPercent(pos) {
  if (!pos || typeof pos !== 'string') return { x: 50, y: 50 }
  const trimmed = pos.trim()
  const pct = trimmed.match(/^(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/)
  if (pct) {
    return {
      x: Math.min(100, Math.max(0, Number(pct[1]))),
      y: Math.min(100, Math.max(0, Number(pct[2]))),
    }
  }
  const preset = {
    'center center': { x: 50, y: 50 },
    'top center': { x: 50, y: 0 },
    'bottom center': { x: 50, y: 100 },
    'center left': { x: 0, y: 50 },
    'center right': { x: 100, y: 50 },
    'top left': { x: 0, y: 0 },
    'top right': { x: 100, y: 0 },
    'bottom left': { x: 0, y: 100 },
    'bottom right': { x: 100, y: 100 },
  }
  return preset[trimmed] ?? { x: 50, y: 50 }
}

function formatPercentPosition(x, y) {
  const rx = Math.round(x * 10) / 10
  const ry = Math.round(y * 10) / 10
  return `${rx}% ${ry}%`
}

export function useBannerFocalDrag(position, setPosition) {
  const sessionRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)

  const onPointerDown = useCallback(
    e => {
      if (e.pointerType === 'mouse' && e.button !== 0) return
      const el = e.currentTarget
      el.setPointerCapture(e.pointerId)
      const rect = el.getBoundingClientRect()
      sessionRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startPos: parsePositionToPercent(position),
        rect,
      }
      setIsDragging(true)
    },
    [position],
  )

  const onPointerMove = useCallback(
    e => {
      const s = sessionRef.current
      if (!s) return
      const dx = e.clientX - s.startX
      const dy = e.clientY - s.startY
      const { width, height } = s.rect
      if (width < 1 || height < 1) return
      const nx = Math.min(
        100,
        Math.max(0, s.startPos.x - (dx / width) * 100),
      )
      const ny = Math.min(
        100,
        Math.max(0, s.startPos.y - (dy / height) * 100),
      )
      setPosition(formatPercentPosition(nx, ny))
    },
    [setPosition],
  )

  const endPan = useCallback(e => {
    if (!sessionRef.current) return
    sessionRef.current = null
    setIsDragging(false)
    try {
      if (e?.currentTarget != null && e.pointerId != null) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }
    } catch (_) {
      /* ignore */
    }
  }, [])

  return {
    panHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endPan,
      onPointerCancel: endPan,
    },
    isDragging,
  }
}

export function ImageFitControls({
  sizeValue,
  positionValue,
  onSizeChange,
  onPositionChange,
}) {
  const selectPositionValue = PRESET_POSITION_VALUES.has(positionValue)
    ? positionValue
    : '__custom__'

  return (
    <div className="change-font-family mt-6 flex w-full max-w-xl flex-col gap-4 sm:flex-row sm:items-end">
      <label className="flex flex-1 flex-col gap-2 text-sm text-[#2B364B]">
        How the image fills the banner
        <select
          className="rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm"
          value={sizeValue}
          onChange={e => onSizeChange(e.target.value)}
        >
          {BANNER_IMAGE_FIT_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-1 flex-col gap-2 text-sm text-[#2B364B]">
        Focal point (or drag preview)
        <select
          className="rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm"
          value={selectPositionValue}
          onChange={e => {
            const v = e.target.value
            if (v && v !== '__custom__') onPositionChange(v)
          }}
        >
          <option value="__custom__" disabled>
            Custom — drag the preview
          </option>
          {BANNER_IMAGE_FOCUS_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  )
}
